import React, { useState, useEffect } from 'react'
import { Card, Row, Col, Tabs, Avatar, Descriptions, Statistic, List, Tag, Space, Button, Empty, Modal, Select, message, Popconfirm } from 'antd'
import {
  UserOutlined,
  BookOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  EditOutlined,
  EyeOutlined,
  HeartOutlined,
  MedicineBoxOutlined,
  CalendarOutlined,
  FileSearchOutlined,
  ShoppingCartOutlined,
  ToolOutlined,
  BarChartOutlined,
  FileProtectOutlined,
  TeamOutlined,
  SettingOutlined,
  LockOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { RootState } from '../../store'
import { setCredentials } from '../../store/slices/authSlice'
import apiClient from '../../api/client'
import { getAvailableRoles, assignRoleToUser, removeRoleFromUser, getUserRoles, type Role } from '../../api/users'
import { getProfileStatistics, type ProfileStatistics as TextbookProfileStatistics } from '../../api/textbook'
import { getRuoyiAdminIndexUrl } from '../../utils/ruoyiAdminUrl'
import './Profile.css'

const { TabPane } = Tabs

interface UserStats {
  // 教材编写相关统计
  textbookStats?: {
    totalTextbooks: number
    totalChapters: number
    totalWords: number
    pendingReview: number
    approvedRate: number
    completedTasks: number
  }
  // 患者相关统计
  patientStats?: {
    totalAppointments: number
    upcomingAppointments: number
    totalRecords: number
    prescriptions: number
  }
  // 医生相关统计
  doctorStats?: {
    totalPatients: number
    todayPatients: number
    totalDiagnoses: number
    totalPrescriptions: number
  }
  // 药剂师相关统计
  pharmacistStats?: {
    pendingReviews: number
    totalReviews: number
    totalDispenses: number
    inventoryAlerts: number
  }
  // 医疗器械师相关统计
  technicianStats?: {
    totalEquipment: number
    maintenanceDue: number
    totalOperations: number
    equipmentStatus: {
      normal: number
      fault: number
      maintenance: number
    }
  }
  // 医疗管理者相关统计
  managerStats?: {
    totalVisits: number
    totalRevenue: number
    departmentCount: number
    staffCount: number
  }
  // 法务相关统计
  legalStats?: {
    totalContracts: number
    expiringContracts: number
    pendingAffairs: number
    complianceChecks: number
  }
}

const Profile: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { user } = useSelector((state: RootState) => state.auth)
  const [loading, setLoading] = useState(false)
  const [stats, setStats] = useState<UserStats>({})
  const [activeTab, setActiveTab] = useState('overview')
  const [userRoles, setUserRoles] = useState<string[]>([])
  
  // 角色管理相关状态
  const [userRoleList, setUserRoleList] = useState<Role[]>([])
  const [availableRoleList, setAvailableRoleList] = useState<Role[]>([])
  const [addRoleModalVisible, setAddRoleModalVisible] = useState(false)
  const [selectedRoleCode, setSelectedRoleCode] = useState<string>('')

  // 从API获取用户信息
  const loadUserInfoFromAPI = async (userId: string | number) => {
    try {
      const response: any = await apiClient.get('/v1/auth/current-user')
      if (response.code === 200 && response.data) {
        const updatedUser = {
          ...response.data,
          id: response.data.id || userId,
          roles: response.data.roles || (response.data.role ? [response.data.role] : []),
        }
        // 更新Redux状态
        dispatch(setCredentials({
          user: updatedUser,
          token: localStorage.getItem('token') || '',
          roles: updatedUser.roles,
        }))
      }
    } catch (error) {
      console.error('从API获取用户信息失败:', error)
      // 如果API失败，使用localStorage中的用户信息
    }
  }

  // 从API获取用户所有角色（确保获取最新角色）
  useEffect(() => {
    // 如果Redux中没有user，但localStorage有token，尝试恢复用户信息
    if (!user) {
      const token = localStorage.getItem('token')
      const userStr = localStorage.getItem('user')
      if (token && userStr) {
        try {
          const savedUser = JSON.parse(userStr)
          const savedRoles = localStorage.getItem('roles')
          const roles = savedRoles ? JSON.parse(savedRoles) : (savedUser.roles || (savedUser.role ? [savedUser.role] : []))
          
          // 恢复Redux状态
          dispatch(setCredentials({
            user: savedUser,
            token: token,
            roles: roles,
          }))
          
          // 尝试从API获取最新用户信息
          if (savedUser.id) {
            loadUserInfoFromAPI(savedUser.id)
          }
        } catch (error) {
          console.error('恢复用户信息失败:', error)
        }
      }
      return
    }
    
    if (user?.id) {
      loadUserRolesFromAPI()
      loadUserStats()
      if (activeTab === 'roles') {
        loadRoleManagementData()
      }
    } else if (user) {
      // 如果没有ID，使用Redux中的角色
      const roles = user.roles || (user.role ? [user.role] : [])
      setUserRoles(roles)
    }
  }, [user?.id, activeTab, user, dispatch])
  
  // 加载角色管理数据
  const loadRoleManagementData = async () => {
    if (!user?.id) return
    try {
      setLoading(true)
      const [roles, availableRoles] = await Promise.all([
        getUserRoles(user.id).catch(err => {
          console.error('获取用户角色失败:', err)
          // 如果401错误，可能是token过期，尝试刷新用户信息
          if (err.response?.status === 401) {
            const token = localStorage.getItem('token')
            if (token) {
              // token存在但API返回401，可能是token过期
              message.warning('登录已过期，请重新登录')
            }
          }
          return [] // 返回空数组，不中断流程
        }),
        getAvailableRoles().catch(err => {
          console.error('获取可用角色失败:', err)
          if (err.response?.status === 401) {
            const token = localStorage.getItem('token')
            if (token) {
              message.warning('登录已过期，请重新登录')
            }
          }
          return [] // 返回空数组，不中断流程
        })
      ])
      setUserRoleList(roles)
      setAvailableRoleList(availableRoles)
    } catch (error: any) {
      console.error('加载角色管理数据失败:', error)
      // 不显示错误消息，因为已经在Promise.catch中处理了
    } finally {
      setLoading(false)
    }
  }
  
  // 添加角色
  const handleAddRole = async () => {
    if (!selectedRoleCode || !user?.id) {
      message.warning('请选择要添加的角色')
      return
    }
    
    try {
      setLoading(true)
      await assignRoleToUser(user.id, selectedRoleCode)
      message.success('角色添加成功')
      setAddRoleModalVisible(false)
      setSelectedRoleCode('')
      // 重新加载数据
      await loadRoleManagementData()
      await loadUserRolesFromAPI()
    } catch (error: any) {
      console.error('添加角色失败:', error)
      message.error('添加角色失败: ' + (error.message || '未知错误'))
    } finally {
      setLoading(false)
    }
  }
  
  // 移除角色
  const handleRemoveRole = async (roleId: number) => {
    if (!user?.id) return
    
    try {
      setLoading(true)
      await removeRoleFromUser(user.id, roleId)
      message.success('角色移除成功')
      // 重新加载数据
      await loadRoleManagementData()
      await loadUserRolesFromAPI()
    } catch (error: any) {
      console.error('移除角色失败:', error)
      message.error('移除角色失败: ' + (error.message || '未知错误'))
    } finally {
      setLoading(false)
    }
  }
  
  // 判断角色是否需要审批
  const isRoleRequiresApproval = (roleCode: string): boolean => {
    const requiresApproval = [
      'DOCTOR',
      'AUTHOR',
      'DEPUTY_EDITOR',
      'EDITOR_IN_CHIEF',
      'REVIEW_COMMITTEE',
      'MEDICAL_MANAGER',
      'LEGAL_AFFAIRS'
    ]
    return requiresApproval.includes(roleCode.toUpperCase())
  }
  
  // 判断角色是否可以直接添加
  const canAddRoleDirectly = (roleCode: string): boolean => {
    const directAddRoles = ['PATIENT', 'READER']
    return directAddRoles.includes(roleCode.toUpperCase())
  }

  // 从API获取用户所有角色
  const loadUserRolesFromAPI = async () => {
    if (!user?.id) return
    
    try {
      const response = await apiClient.get(`/v1/auth/user/${user.id}/roles`)
      if (response && Array.isArray(response)) {
        const roleCodes = response.map((r: any) => r.roleCode || r.roleKey).filter(Boolean)
        if (roleCodes.length > 0) {
          setUserRoles(roleCodes)
        } else {
          // 如果API返回空，使用Redux中的角色
          const roles = user?.roles || (user?.role ? [user.role] : [])
          setUserRoles(roles)
        }
      }
    } catch (error) {
      console.error('加载用户角色失败:', error)
      // 如果API失败，使用Redux中的角色
      const roles = user?.roles || (user?.role ? [user.role] : [])
      setUserRoles(roles)
    }
  }

  // 获取用户角色，优先从state中的userRoles获取，其次从Redux store中的user.roles获取
  const userRoleCodes = userRoles.length > 0 ? userRoles : (user?.roles || (user?.role ? [user.role] : []))
  
  // 判断用户有哪些角色（使用userRoleCodes）
  const hasTextbookRole = userRoleCodes.some(role => 
    ['AUTHOR', 'DEPUTY_EDITOR', 'EDITOR_IN_CHIEF', 'REVIEW_COMMITTEE', 'READER'].includes(role.toUpperCase())
  )
  const hasPatientRole = userRoleCodes.includes('PATIENT') || userRoleCodes.includes('patient')
  const hasDoctorRole = userRoleCodes.includes('DOCTOR') || userRoleCodes.includes('doctor')
  const hasPharmacistRole = userRoleCodes.includes('PHARMACIST') || userRoleCodes.includes('pharmacist')
  const hasTechnicianRole = userRoleCodes.includes('MEDICAL_EQUIPMENT_TECHNICIAN') || userRoleCodes.includes('medical_equipment_technician')
  const hasManagerRole = userRoleCodes.includes('MEDICAL_MANAGER') || userRoleCodes.includes('medical_manager')
  const hasLegalRole = userRoleCodes.includes('LEGAL_AFFAIRS') || userRoleCodes.includes('legal_affairs')

  // 获取用户统计数据
  useEffect(() => {
    if (user) {
      loadUserStats()
    }
  }, [user])

  const loadUserStats = async () => {
    if (!user?.id) return
    
    setLoading(true)
    try {
      const stats: UserStats = {}
      
      // 教材编写统计（如果有教材编写角色）
      if (hasTextbookRole) {
        try {
          const textbookStats = await getProfileStatistics(user.id)
          stats.textbookStats = {
            totalTextbooks: textbookStats.totalTextbooks || 0,
            totalChapters: textbookStats.totalChapters || 0,
            totalWords: textbookStats.totalWords || 0,
            pendingReview: textbookStats.pendingReview || 0,
            approvedRate: textbookStats.approvedRate || 0,
            completedTasks: textbookStats.completedTasks || 0,
            inProgressTasks: textbookStats.inProgressTasks || 0,
            pendingTasks: textbookStats.pendingTasks || 0,
            overdueTasks: textbookStats.overdueTasks || 0,
            approvedChapters: textbookStats.approvedChapters || 0,
            rejectedChapters: textbookStats.rejectedChapters || 0
          }
        } catch (error) {
          console.error('获取教材编写统计失败:', error)
          // 使用默认值
          stats.textbookStats = {
            totalTextbooks: 0,
            totalChapters: 0,
            totalWords: 0,
            pendingReview: 0,
            approvedRate: 0,
            completedTasks: 0,
            inProgressTasks: 0,
            pendingTasks: 0,
            overdueTasks: 0,
            approvedChapters: 0,
            rejectedChapters: 0
          }
        }
      }
      
      if (hasPatientRole) {
        mockStats.patientStats = {
          totalAppointments: 15,
          upcomingAppointments: 2,
          totalRecords: 12,
          prescriptions: 8,
        }
      }
      
      if (hasDoctorRole) {
        mockStats.doctorStats = {
          totalPatients: 156,
          todayPatients: 8,
          totalDiagnoses: 234,
          totalPrescriptions: 189,
        }
      }
      
      if (hasPharmacistRole) {
        mockStats.pharmacistStats = {
          pendingReviews: 5,
          totalReviews: 156,
          totalDispenses: 1234,
          inventoryAlerts: 3,
        }
      }
      
      if (hasTechnicianRole) {
        mockStats.technicianStats = {
          totalEquipment: 45,
          maintenanceDue: 3,
          totalOperations: 567,
          equipmentStatus: {
            normal: 40,
            fault: 2,
            maintenance: 3,
          },
        }
      }
      
      if (hasManagerRole) {
        mockStats.managerStats = {
          totalVisits: 1234,
          totalRevenue: 456789,
          departmentCount: 12,
          staffCount: 156,
        }
      }
      
      if (hasLegalRole) {
        mockStats.legalStats = {
          totalContracts: 23,
          expiringContracts: 2,
          pendingAffairs: 5,
          complianceChecks: 8,
        }
      }
      
      setStats(mockStats)
    } catch (error) {
      console.error('加载统计数据失败:', error)
    } finally {
      setLoading(false)
    }
  }

  // 获取角色显示名称
  const getRoleDisplayName = (role: string): string => {
    const roleMap: Record<string, string> = {
      'AUTHOR': '教材编写人员',
      'DEPUTY_EDITOR': '副主编',
      'EDITOR_IN_CHIEF': '主编',
      'REVIEW_COMMITTEE': '审稿委员会',
      'READER': '读者',
      'PATIENT': '患者',
      'DOCTOR': '医生',
      'PHARMACIST': '药剂师',
      'MEDICAL_EQUIPMENT_TECHNICIAN': '医疗器械师',
      'MEDICAL_MANAGER': '医疗管理者',
      'LEGAL_AFFAIRS': '法务',
      'ADMIN': '管理员',
    }
    return roleMap[role.toUpperCase()] || role
  }

  // 获取角色图标
  const getRoleIcon = (role: string): React.ReactNode => {
    const roleUpper = role.toUpperCase()
    if (['AUTHOR', 'DEPUTY_EDITOR', 'EDITOR_IN_CHIEF', 'REVIEW_COMMITTEE', 'READER'].includes(roleUpper)) {
      return <BookOutlined />
    } else if (roleUpper === 'PATIENT') {
      return <HeartOutlined />
    } else if (roleUpper === 'DOCTOR') {
      return <MedicineBoxOutlined />
    } else if (roleUpper === 'PHARMACIST') {
      return <MedicineBoxOutlined />
    } else if (roleUpper === 'MEDICAL_EQUIPMENT_TECHNICIAN') {
      return <ToolOutlined />
    } else if (roleUpper === 'MEDICAL_MANAGER') {
      return <BarChartOutlined />
    } else if (roleUpper === 'LEGAL_AFFAIRS') {
      return <FileProtectOutlined />
    } else if (roleUpper === 'ADMIN') {
      return <SettingOutlined />
    }
    return <UserOutlined />
  }

  // 获取角色跳转路径
  const getRolePath = (role: string): string => {
    const roleUpper = role.toUpperCase()
    
    // 教材编写角色 - 根据具体角色跳转到对应功能页面
    if (roleUpper === 'AUTHOR') {
      // 编者：我的任务页
      return '/textbook/tasks'
    } else if (roleUpper === 'DEPUTY_EDITOR') {
      // 副主编：待审核章节列表
      return '/textbook/review/pending'
    } else if (roleUpper === 'EDITOR_IN_CHIEF') {
      // 主编：教材管理页
      return '/textbook/manage'
    } else if (roleUpper === 'REVIEW_COMMITTEE') {
      // 审稿委员会：待审稿章节列表
      return '/textbook/review/pending'
    } else if (roleUpper === 'READER') {
      // 读者：教材列表页
      return '/textbook'
    } else if (roleUpper === 'PATIENT') {
      return '/hospital/appointment'
    } else if (roleUpper === 'DOCTOR') {
      return '/hospital/patients/today'
    } else if (roleUpper === 'PHARMACIST') {
      return '/hospital/prescriptions/review/pending'
    } else if (roleUpper === 'MEDICAL_EQUIPMENT_TECHNICIAN') {
      return '/hospital/equipment'
    } else if (roleUpper === 'MEDICAL_MANAGER') {
      return '/hospital/management/dashboard'
    } else if (roleUpper === 'LEGAL_AFFAIRS') {
      return '/hospital/legal/contracts'
    } else if (roleUpper === 'ADMIN') {
      // 实际跳转在 handleRoleClick 中打开若依管理端（外部 URL）
      return '/profile'
    }
    return '/'
  }

  // 处理角色点击
  const handleRoleClick = (role: string) => {
    if (role.toUpperCase() === 'ADMIN') {
      window.location.assign(getRuoyiAdminIndexUrl())
      return
    }
    const path = getRolePath(role)
    navigate(path)
  }

  // 渲染教材编写统计卡片
  const renderTextbookStats = () => {
    if (!hasTextbookRole || !stats.textbookStats) return null

    const { textbookStats } = stats
    const isAuthor = userRoleCodes.some(r => ['AUTHOR', 'author'].includes(r))
    const isEditor = userRoleCodes.some(r => ['DEPUTY_EDITOR', 'EDITOR_IN_CHIEF', 'deputy_editor', 'editor_in_chief'].includes(r))
    const isReader = userRoleCodes.some(r => ['READER', 'reader'].includes(r))

    return (
      <Card title={<><BookOutlined /> 教材编写统计</>} className="profile-card">
        <Row gutter={16}>
          {isAuthor && (
            <>
              <Col span={6}>
                <Statistic title="编写的教材" value={textbookStats.totalTextbooks} />
              </Col>
              <Col span={6}>
                <Statistic title="编写的章节" value={textbookStats.totalChapters} />
              </Col>
              <Col span={6}>
                <Statistic title="总字数" value={textbookStats.totalWords} suffix="字" />
              </Col>
              <Col span={6}>
                <Statistic title="已完成任务" value={textbookStats.completedTasks} />
              </Col>
              {textbookStats.inProgressTasks !== undefined && (
                <Col span={6}>
                  <Statistic 
                    title="进行中任务" 
                    value={textbookStats.inProgressTasks} 
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Col>
              )}
              {textbookStats.pendingTasks !== undefined && (
                <Col span={6}>
                  <Statistic 
                    title="待开始任务" 
                    value={textbookStats.pendingTasks} 
                    valueStyle={{ color: '#faad14' }}
                  />
                </Col>
              )}
              {textbookStats.overdueTasks !== undefined && textbookStats.overdueTasks > 0 && (
                <Col span={6}>
                  <Statistic 
                    title="已逾期任务" 
                    value={textbookStats.overdueTasks} 
                    valueStyle={{ color: '#ff4d4f' }}
                  />
                </Col>
              )}
            </>
          )}
          {isEditor && (
            <Col span={6}>
              <Statistic 
                title="待审核章节" 
                value={textbookStats.pendingReview} 
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Col>
          )}
          {(isAuthor || isEditor) && (
            <Col span={6}>
              <Statistic 
                title="审核通过率" 
                value={textbookStats.approvedRate} 
                suffix="%" 
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
          )}
          {textbookStats.approvedChapters !== undefined && (
            <Col span={6}>
              <Statistic 
                title="已通过章节" 
                value={textbookStats.approvedChapters} 
                valueStyle={{ color: '#52c41a' }}
              />
            </Col>
          )}
          {textbookStats.rejectedChapters !== undefined && (
            <Col span={6}>
              <Statistic 
                title="已拒绝章节" 
                value={textbookStats.rejectedChapters} 
                valueStyle={{ color: '#ff4d4f' }}
              />
            </Col>
          )}
        </Row>
        <div style={{ marginTop: 16 }}>
          <Space>
            {isAuthor && (
              <Button 
                type="primary" 
                icon={<EditOutlined />}
                onClick={() => navigate('/textbook')}
              >
                我的教材
              </Button>
            )}
            {isAuthor && (
              <Button 
                icon={<FileTextOutlined />}
                onClick={() => navigate('/textbook/tasks')}
              >
                我的任务
              </Button>
            )}
            {isEditor && (
              <Button 
                icon={<CheckCircleOutlined />}
                onClick={() => navigate('/textbook/review/pending')}
              >
                待审核章节
              </Button>
            )}
            {isReader && (
              <Button 
                icon={<EyeOutlined />}
                onClick={() => navigate('/textbook')}
              >
                浏览教材
              </Button>
            )}
          </Space>
        </div>
      </Card>
    )
  }

  // 渲染患者统计卡片
  const renderPatientStats = () => {
    if (!hasPatientRole || !stats.patientStats) return null

    const { patientStats } = stats

    return (
      <Card title={<><HeartOutlined /> 就诊统计</>} className="profile-card">
        <Row gutter={16}>
          <Col span={6}>
            <Statistic title="总预约次数" value={patientStats.totalAppointments} />
          </Col>
          <Col span={6}>
            <Statistic 
              title="即将就诊" 
              value={patientStats.upcomingAppointments} 
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={6}>
            <Statistic title="就诊记录" value={patientStats.totalRecords} />
          </Col>
          <Col span={6}>
            <Statistic title="处方数量" value={patientStats.prescriptions} />
          </Col>
        </Row>
        <div style={{ marginTop: 16 }}>
          <Space>
            <Button 
              type="primary" 
              icon={<CalendarOutlined />}
              onClick={() => navigate('/hospital/appointment')}
            >
              预约挂号
            </Button>
            <Button 
              icon={<FileSearchOutlined />}
              onClick={() => navigate('/hospital/records')}
            >
              就诊记录
            </Button>
            <Button 
              icon={<ShoppingCartOutlined />}
              onClick={() => navigate('/hospital/prescriptions')}
            >
              我的处方
            </Button>
          </Space>
        </div>
      </Card>
    )
  }

  // 渲染医生统计卡片
  const renderDoctorStats = () => {
    if (!hasDoctorRole || !stats.doctorStats) return null

    const { doctorStats } = stats

    return (
      <Card title={<><MedicineBoxOutlined /> 工作统计</>} className="profile-card">
        <Row gutter={16}>
          <Col span={6}>
            <Statistic title="总患者数" value={doctorStats.totalPatients} />
          </Col>
          <Col span={6}>
            <Statistic 
              title="今日患者" 
              value={doctorStats.todayPatients} 
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={6}>
            <Statistic title="总诊断数" value={doctorStats.totalDiagnoses} />
          </Col>
          <Col span={6}>
            <Statistic title="总处方数" value={doctorStats.totalPrescriptions} />
          </Col>
        </Row>
        <div style={{ marginTop: 16 }}>
          <Space>
            <Button 
              type="primary" 
              icon={<TeamOutlined />}
              onClick={() => navigate('/hospital/patients/today')}
            >
              今日患者
            </Button>
            <Button 
              icon={<MedicineBoxOutlined />}
              onClick={() => navigate('/hospital/diagnosis/workspace')}
            >
              诊断工作台
            </Button>
            <Button 
              icon={<BarChartOutlined />}
              onClick={() => navigate('/hospital/statistics/work')}
            >
              工作统计
            </Button>
          </Space>
        </div>
      </Card>
    )
  }

  // 渲染药剂师统计卡片
  const renderPharmacistStats = () => {
    if (!hasPharmacistRole || !stats.pharmacistStats) return null

    const { pharmacistStats } = stats

    return (
      <Card title={<><MedicineBoxOutlined /> 工作统计</>} className="profile-card">
        <Row gutter={16}>
          <Col span={6}>
            <Statistic 
              title="待审核处方" 
              value={pharmacistStats.pendingReviews} 
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Col>
          <Col span={6}>
            <Statistic title="总审核数" value={pharmacistStats.totalReviews} />
          </Col>
          <Col span={6}>
            <Statistic title="总配药数" value={pharmacistStats.totalDispenses} />
          </Col>
          <Col span={6}>
            <Statistic 
              title="库存预警" 
              value={pharmacistStats.inventoryAlerts} 
              valueStyle={{ color: '#faad14' }}
            />
          </Col>
        </Row>
        <div style={{ marginTop: 16 }}>
          <Space>
            <Button 
              type="primary" 
              icon={<CheckCircleOutlined />}
              onClick={() => navigate('/hospital/prescriptions/review/pending')}
            >
              待审核处方
            </Button>
            <Button 
              icon={<ToolOutlined />}
              onClick={() => navigate('/hospital/pharmacy/dispense/pending')}
            >
              待配药列表
            </Button>
            <Button 
              icon={<MedicineBoxOutlined />}
              onClick={() => navigate('/hospital/pharmacy/inventory')}
            >
              药品库存
            </Button>
          </Space>
        </div>
      </Card>
    )
  }

  // 渲染医疗器械师统计卡片
  const renderTechnicianStats = () => {
    if (!hasTechnicianRole || !stats.technicianStats) return null

    const { technicianStats } = stats

    return (
      <Card title={<><ToolOutlined /> 设备统计</>} className="profile-card">
        <Row gutter={16}>
          <Col span={6}>
            <Statistic title="设备总数" value={technicianStats.totalEquipment} />
          </Col>
          <Col span={6}>
            <Statistic 
              title="待维护设备" 
              value={technicianStats.maintenanceDue} 
              valueStyle={{ color: '#faad14' }}
            />
          </Col>
          <Col span={6}>
            <Statistic title="总操作次数" value={technicianStats.totalOperations} />
          </Col>
          <Col span={6}>
            <Statistic 
              title="故障设备" 
              value={technicianStats.equipmentStatus.fault} 
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Col>
        </Row>
        <div style={{ marginTop: 16 }}>
          <Space>
            <Button 
              type="primary" 
              icon={<ToolOutlined />}
              onClick={() => navigate('/hospital/equipment')}
            >
              设备管理
            </Button>
            <Button 
              icon={<ClockCircleOutlined />}
              onClick={() => navigate('/hospital/equipment/maintenance/plan')}
            >
              维护计划
            </Button>
            <Button 
              icon={<BarChartOutlined />}
              onClick={() => navigate('/hospital/equipment/statistics')}
            >
              设备统计
            </Button>
          </Space>
        </div>
      </Card>
    )
  }

  // 渲染医疗管理者统计卡片
  const renderManagerStats = () => {
    if (!hasManagerRole || !stats.managerStats) return null

    const { managerStats } = stats

    return (
      <Card title={<><BarChartOutlined /> 管理统计</>} className="profile-card">
        <Row gutter={16}>
          <Col span={6}>
            <Statistic title="总就诊数" value={managerStats.totalVisits} />
          </Col>
          <Col span={6}>
            <Statistic 
              title="总收入" 
              value={managerStats.totalRevenue} 
              prefix="¥" 
              precision={2}
            />
          </Col>
          <Col span={6}>
            <Statistic title="科室数量" value={managerStats.departmentCount} />
          </Col>
          <Col span={6}>
            <Statistic title="员工数量" value={managerStats.staffCount} />
          </Col>
        </Row>
        <div style={{ marginTop: 16 }}>
          <Space>
            <Button 
              type="primary" 
              icon={<BarChartOutlined />}
              onClick={() => navigate('/hospital/management/dashboard')}
            >
              数据概览
            </Button>
            <Button 
              icon={<TeamOutlined />}
              onClick={() => navigate('/hospital/management/staff')}
            >
              人员管理
            </Button>
            <Button 
              icon={<SettingOutlined />}
              onClick={() => navigate('/hospital/management/departments')}
            >
              科室管理
            </Button>
          </Space>
        </div>
      </Card>
    )
  }

  // 渲染法务统计卡片
  const renderLegalStats = () => {
    if (!hasLegalRole || !stats.legalStats) return null

    const { legalStats } = stats

    return (
      <Card title={<><FileProtectOutlined /> 工作统计</>} className="profile-card">
        <Row gutter={16}>
          <Col span={6}>
            <Statistic title="合同总数" value={legalStats.totalContracts} />
          </Col>
          <Col span={6}>
            <Statistic 
              title="即将到期" 
              value={legalStats.expiringContracts} 
              valueStyle={{ color: '#faad14' }}
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="待处理事务" 
              value={legalStats.pendingAffairs} 
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Col>
          <Col span={6}>
            <Statistic title="合规检查" value={legalStats.complianceChecks} />
          </Col>
        </Row>
        <div style={{ marginTop: 16 }}>
          <Space>
            <Button 
              type="primary" 
              icon={<FileProtectOutlined />}
              onClick={() => navigate('/hospital/legal/contracts')}
            >
              合同管理
            </Button>
            <Button 
              icon={<FileTextOutlined />}
              onClick={() => navigate('/hospital/legal/affairs')}
            >
              法律事务
            </Button>
            <Button 
              icon={<CheckCircleOutlined />}
              onClick={() => navigate('/hospital/legal/compliance/checks')}
            >
              合规检查
            </Button>
          </Space>
        </div>
      </Card>
    )
  }

  // 获取标签页配置
  const getTabPanes = () => {
    const panes: Array<{ key: string; label: string; icon: React.ReactNode; content: React.ReactNode }> = []

    // 概览标签页
    panes.push({
      key: 'overview',
      label: '概览',
      icon: <UserOutlined />,
      content: (
        <div>
          {/* 角色卡片区域 */}
          {userRoleCodes.length > 0 && (
            <Card 
              title={
                <span>
                  <UserOutlined style={{ marginRight: 8 }} />
                  我的角色
                </span>
              }
              className="profile-card"
              style={{ marginBottom: 24 }}
            >
              <Row gutter={[16, 16]}>
                {userRoleCodes.map(role => {
                  const roleDisplayName = getRoleDisplayName(role)
                  const roleIcon = getRoleIcon(role)
                  const rolePath = getRolePath(role)
                  
                  return (
                    <Col key={role} xs={24} sm={12} md={8} lg={6}>
                      <Card
                        hoverable
                        className="role-card"
                        onClick={() => handleRoleClick(role)}
                        style={{
                          cursor: 'pointer',
                          textAlign: 'center',
                          height: '100%',
                          transition: 'all 0.3s',
                          border: '1px solid #e8e8e8',
                        }}
                        bodyStyle={{
                          padding: '24px 16px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minHeight: '140px',
                        }}
                      >
                        <div 
                          style={{ 
                            fontSize: 40, 
                            marginBottom: 16, 
                            color: '#1890ff',
                            transition: 'transform 0.3s',
                          }}
                          className="role-icon"
                        >
                          {roleIcon}
                        </div>
                        <div 
                          style={{ 
                            fontSize: 16, 
                            fontWeight: 600, 
                            marginBottom: 8,
                            color: '#262626',
                          }}
                        >
                          {roleDisplayName}
                        </div>
                        <div 
                          style={{ 
                            fontSize: 12, 
                            color: '#8c8c8c',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                          }}
                        >
                          <span>点击进入</span>
                          <span>→</span>
                        </div>
                      </Card>
                    </Col>
                  )
                })}
              </Row>
            </Card>
          )}

          {/* 统计数据区域 */}
          {renderTextbookStats()}
          {renderPatientStats()}
          {renderDoctorStats()}
          {renderPharmacistStats()}
          {renderTechnicianStats()}
          {renderManagerStats()}
          {renderLegalStats()}
          {!hasTextbookRole && !hasPatientRole && !hasDoctorRole && 
           !hasPharmacistRole && !hasTechnicianRole && !hasManagerRole && !hasLegalRole && (
            <Card>
              <Empty description="暂无统计数据" />
            </Card>
          )}
        </div>
      ),
    })

    // 角色管理标签页
    panes.push({
      key: 'roles',
      label: '角色管理',
      icon: <TeamOutlined />,
      content: (
        <div>
          <Card 
            title={
              <span>
                <TeamOutlined style={{ marginRight: 8 }} />
                我的角色
              </span>
            }
            extra={
              <Button 
                type="primary" 
                icon={<PlusOutlined />}
                onClick={async () => {
                  // 打开模态框前，确保数据已加载
                  if (availableRoleList.length === 0) {
                    await loadRoleManagementData()
                  }
                  setAddRoleModalVisible(true)
                }}
              >
                添加角色
              </Button>
            }
            style={{ marginBottom: 24 }}
          >
            {userRoleList.length === 0 ? (
              <Empty description="暂无角色，请添加角色" />
            ) : (
              <List
                dataSource={userRoleList}
                loading={loading}
                renderItem={(role) => {
                  const roleCode = role.roleCode || ''
                  const isAdmin = roleCode.toUpperCase() === 'ADMIN'
                  const requiresApproval = isRoleRequiresApproval(roleCode)
                  
                  return (
                    <List.Item
                      actions={[
                        !isAdmin && (
                          <Popconfirm
                            title="确定要移除该角色吗？"
                            onConfirm={() => handleRemoveRole(role.id)}
                            okText="确定"
                            cancelText="取消"
                          >
                            <Button 
                              type="link" 
                              danger 
                              icon={<DeleteOutlined />}
                              disabled={loading}
                            >
                              移除
                            </Button>
                          </Popconfirm>
                        ),
                      ].filter(Boolean)}
                    >
                      <List.Item.Meta
                        avatar={
                          <Tag color={requiresApproval ? 'orange' : 'blue'} style={{ fontSize: 16, padding: '4px 12px' }}>
                            {role.roleName}
                          </Tag>
                        }
                        title={
                          <Space>
                            <span>{role.roleName}</span>
                            {requiresApproval && (
                              <Tag color="orange" size="small">需审批</Tag>
                            )}
                            {isAdmin && (
                              <Tag color="red" size="small">系统管理员</Tag>
                            )}
                          </Space>
                        }
                        description={
                          <div>
                            <div style={{ marginBottom: 4 }}>
                              <Tag>角色代码: {roleCode}</Tag>
                              <Tag>角色类型: {role.roleType}</Tag>
                            </div>
                            {role.description && (
                              <div style={{ color: '#666', fontSize: 12 }}>
                                {role.description}
                              </div>
                            )}
                          </div>
                        }
                      />
                    </List.Item>
                  )
                }}
              />
            )}
          </Card>
          
          <Card title="可申请的角色">
            <List
              dataSource={availableRoleList.filter(role => 
                !userRoleList.some(userRole => userRole.id === role.id)
              )}
              loading={loading}
              renderItem={(role) => {
                const roleCode = role.roleCode || ''
                const requiresApproval = isRoleRequiresApproval(roleCode)
                const canAdd = canAddRoleDirectly(roleCode)
                
                return (
                  <List.Item
                    actions={[
                      <Button 
                        type="link" 
                        icon={<PlusOutlined />}
                        onClick={async () => {
                          // 打开模态框前，确保数据已加载
                          if (availableRoleList.length === 0) {
                            await loadRoleManagementData()
                          }
                          setSelectedRoleCode(roleCode)
                          setAddRoleModalVisible(true)
                        }}
                        disabled={loading}
                      >
                        {canAdd ? '直接添加' : '申请添加'}
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={
                        <Tag color={requiresApproval ? 'orange' : 'green'} style={{ fontSize: 16, padding: '4px 12px' }}>
                          {role.roleName}
                        </Tag>
                      }
                      title={
                        <Space>
                          <span>{role.roleName}</span>
                          {requiresApproval && (
                            <Tag color="orange" size="small">需审批</Tag>
                          )}
                          {canAdd && (
                            <Tag color="green" size="small">可直接添加</Tag>
                          )}
                        </Space>
                      }
                      description={
                        <div>
                          <div style={{ marginBottom: 4 }}>
                            <Tag>角色代码: {roleCode}</Tag>
                            <Tag>角色类型: {role.roleType}</Tag>
                          </div>
                          {role.description && (
                            <div style={{ color: '#666', fontSize: 12 }}>
                                {role.description}
                            </div>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )
              }}
            />
          </Card>
          
          {/* 添加角色模态框 */}
          <Modal
            title="添加角色"
            open={addRoleModalVisible}
            onOk={handleAddRole}
            onCancel={() => {
              setAddRoleModalVisible(false)
              setSelectedRoleCode('')
            }}
            afterOpenChange={(open) => {
              // 模态框打开时，如果数据未加载，则加载数据
              if (open && availableRoleList.length === 0 && user?.id) {
                loadRoleManagementData()
              }
            }}
            confirmLoading={loading}
            okText="确定"
            cancelText="取消"
          >
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', marginBottom: 8 }}>选择角色：</label>
              <Select
                style={{ width: '100%' }}
                placeholder={availableRoleList.length === 0 && loading ? "正在加载角色列表..." : "请选择要添加的角色"}
                value={selectedRoleCode}
                onChange={setSelectedRoleCode}
                showSearch
                loading={loading && availableRoleList.length === 0}
                notFoundContent={
                  loading && availableRoleList.length === 0 
                    ? "加载中..." 
                    : availableRoleList.length === 0 
                    ? "暂无可用角色，请刷新页面重试" 
                    : null
                }
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={availableRoleList
                  .filter(role => {
                    // 过滤掉已拥有的角色和没有roleCode的角色
                    const hasRole = userRoleList.some(userRole => userRole.id === role.id)
                    const hasRoleCode = role.roleCode && role.roleCode.trim() !== ''
                    return !hasRole && hasRoleCode
                  })
                  .map(role => ({
                    value: role.roleCode || '',
                    label: `${role.roleName} (${role.roleCode || 'N/A'})`,
                  }))}
              />
              {availableRoleList.length === 0 && !loading && (
                <div style={{ marginTop: 8, color: '#ff4d4f', fontSize: 12 }}>
                  无法加载角色列表，请刷新页面重试或联系管理员
                </div>
              )}
            </div>
            {selectedRoleCode && (
              <div style={{ padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
                {isRoleRequiresApproval(selectedRoleCode) ? (
                  <div>
                    <Tag color="orange">该角色需要管理员审批</Tag>
                    <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                      提交申请后，管理员将审核您的申请。审核通过后，角色将自动生效。
                    </div>
                  </div>
                ) : (
                  <div>
                    <Tag color="green">该角色可以直接添加</Tag>
                    <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                      点击确定后，角色将立即生效。
                    </div>
                  </div>
                )}
              </div>
            )}
          </Modal>
        </div>
      ),
    })

    // 个人信息标签页
    panes.push({
      key: 'info',
      label: '个人信息',
      icon: <UserOutlined />,
      content: (
        <Card title="基本信息">
          <Descriptions column={2} bordered>
            <Descriptions.Item label="用户名">{user?.username}</Descriptions.Item>
            <Descriptions.Item label="邮箱">{user?.email}</Descriptions.Item>
            <Descriptions.Item label="角色" span={2}>
              <Space>
                {userRoleCodes.map(role => (
                  <Tag key={role} color="blue">
                    {getRoleDisplayName(role)}
                  </Tag>
                ))}
              </Space>
            </Descriptions.Item>
            {hasDoctorRole && (
              <Descriptions.Item label="执业医师资格证编号" span={2}>
                {/* TODO: 从用户信息中获取 */}
                待完善
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>
      ),
    })

    // 账号设置标签页
    panes.push({
      key: 'settings',
      label: '账号设置',
      icon: <SettingOutlined />,
      content: (
        <Card title="账号设置">
          <List>
            <List.Item
              actions={[
                <Button key="edit" type="link" icon={<EditOutlined />}>
                  编辑
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={<UserOutlined />}
                title="修改个人信息"
                description="修改用户名、邮箱等基本信息"
              />
            </List.Item>
            <List.Item
              actions={[
                <Button key="change" type="link" icon={<LockOutlined />}>
                  修改
                </Button>,
              ]}
            >
              <List.Item.Meta
                avatar={<LockOutlined />}
                title="修改密码"
                description="定期更换密码以保护账号安全"
              />
            </List.Item>
          </List>
        </Card>
      ),
    })

    return panes
  }

  // 如果Redux中没有user，但localStorage有token，显示加载中
  if (!user) {
    const token = localStorage.getItem('token')
    if (token) {
      return (
        <div style={{ padding: 50, textAlign: 'center' }}>
          <Empty description="正在加载用户信息..." />
        </div>
      )
    }
    return (
      <div style={{ padding: 50, textAlign: 'center' }}>
        <Empty description="请先登录" />
      </div>
    )
  }

  const tabPanes = getTabPanes()

  return (
    <div className="profile-page">
      <div className="profile-header">
        <Card>
          <Row gutter={24} align="middle">
            <Col>
              <Avatar size={80} icon={<UserOutlined />} src={user.avatar} />
            </Col>
            <Col flex={1}>
              <h2>{user.username}</h2>
              <Space>
                {userRoleCodes.map(role => (
                  <Tag key={role} color="blue" style={{ fontSize: 14 }}>
                    {getRoleDisplayName(role)}
                  </Tag>
                ))}
              </Space>
              <p style={{ marginTop: 8, color: '#666' }}>{user.email}</p>
            </Col>
          </Row>
        </Card>
      </div>

      <div className="profile-content">
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabPanes.map(pane => ({
            key: pane.key,
            label: (
              <span>
                {pane.icon} {pane.label}
              </span>
            ),
            children: pane.content,
          }))}
        />
      </div>
    </div>
  )
}

export default Profile

